import { useState, useEffect } from "react";
import { format, startOfWeek, startOfMonth, startOfYear } from "date-fns";
import { config } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";
import { apiRequest } from "./utils/apiClient";
import { Spinner, Dropdown, Modal, Button } from "react-bootstrap";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";

const getFilterRange = (filter) => {
  const today = new Date();
  let startDate = today;

  if (filter === "This Week") {
    startDate = startOfWeek(today, { weekStartsOn: 1 });
  } else if (filter === "This Month") {
    startDate = startOfMonth(today);
  } else if (filter === "This Year") {
    startDate = startOfYear(today);
  }

  return {
    start: format(startDate, "yyyy-MM-dd"),
    end: format(today, "yyyy-MM-dd"),
  };
};

const compactNumberFormat = (value) => {
  const trimTrailingZeros = (numericString) => numericString.replace(/\.?0+$/, "");
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return "0";

  const absValue = Math.abs(numericValue);
  if (absValue >= 1_000_000_000) {
    return `${trimTrailingZeros((numericValue / 1_000_000_000).toFixed(3))}B`;
  }
  if (absValue >= 1_000_000) {
    return `${trimTrailingZeros((numericValue / 1_000_000).toFixed(3))}M`;
  }
  if (absValue >= 1_000) {
    return `${trimTrailingZeros((numericValue / 1_000).toFixed(3))}K`;
  }
  return trimTrailingZeros(numericValue.toFixed(3));
};

const Dashboard = () => {
  const [ companies, setCompanies ] = useState(null);
  const [ subcompanies, setSubcompanies ] = useState(null);
  const [ quotations, setQuotations ] = useState(null);
  const [ users, setUsers ] = useState(null);
  const [ grossRevenue, setGrossRevenue ] = useState(null);
  const [ grossProfit, setGrossProfit ] = useState(null);
  const [ momRevenueValue, setMomRevenueValue ] = useState(null);
  const [ momRevenuePercent, setMomRevenuePercent ] = useState(null);
  const [ momProfitValue, setMomProfitValue ] = useState(null);
  const [ momProfitPercent, setMomProfitPercent ] = useState(null);
  const [ wonQuotationCount, setWonQuotationCount ] = useState(null);
  const [ winRate, setWinRate ] = useState(null);
  const [ qualifiedLeadsCount, setQualifiedLeadsCount ] = useState(null);
  const [ qualifiedLeadsAmount, setQualifiedLeadsAmount ] = useState(null);
  const [ dailyRevenueProfitData, setDailyRevenueProfitData ] = useState([]);
  const [ cumulativeRevenueProfitData, setCumulativeRevenueProfitData ] = useState([]);
  const [ topSalesPerformanceData, setTopSalesPerformanceData ] = useState([]);
  const [ topProductPerformanceData, setTopProductPerformanceData ] = useState([]);
  const [ topCompanyGroupsData, setTopCompanyGroupsData ] = useState([]);
  const [ quotationStatusCountData, setQuotationStatusCountData ] = useState([]);
  const [ productTypeDistributionData, setProductTypeDistributionData ] = useState([]);
  const [ productCategoryDistributionData, setProductCategoryDistributionData ] = useState([]);
  const [ orderTypeDistributionData, setOrderTypeDistributionData ] = useState([]);
  const [ paymentTypeDistributionData, setPaymentTypeDistributionData ] = useState([]);
  const [ isLoadingQuotationStats, setIsLoadingQuotationStats ] = useState(false);
  const [ isLoadingLeadStats, setIsLoadingLeadStats ] = useState(false);
  const [ scheduleAlerts, setScheduleAlerts ] = useState([]);
  const [ isLoadingScheduleAlerts, setIsLoadingScheduleAlerts ] = useState(false);
  const [ dateFilter, setDateFilter ] = useState("This Year");
  const [ dateStart, setDateStart ] = useState(getFilterRange("This Year").start);
  const [ dateEnd, setDateEnd ] = useState(getFilterRange("This Year").end);
  const [ showDateModal, setShowDateModal ] = useState(false);
  const [ modalDateStart, setModalDateStart ] = useState(getFilterRange("This Year").start);
  const [ modalDateEnd, setModalDateEnd ] = useState(getFilterRange("This Year").end);

  const crmUser = JSON.parse(localStorage.getItem('crmUser'));

  useEffect(() => {
    getCompanies();
    getSubcompanies();
    getQuotations();
    getUsers();
    getContractScheduleAlerts();
  }, []);

  useEffect(() => {
    if (dateFilter !== "Custom Range") {
      const range = getFilterRange(dateFilter);
      setDateStart(range.start);
      setDateEnd(range.end);
    }
  }, [dateFilter]);

  useEffect(() => {
    if (!dateStart || !dateEnd) {
      return;
    }
    getQuotationStats();
    getLeadStats();
  }, [dateStart, dateEnd]);

  const getCompanies = async () => {
    try {
      const fetchCompanies = await apiRequest(config.ENDPOINT + 'get/company', {
        method: 'POST',
        body: JSON.stringify({})
      });
      const responseGetCompanies = await fetchCompanies.json();
      if (responseGetCompanies.status === 200) {
        setCompanies(responseGetCompanies.data);
      } else {
        alert(responseGetCompanies.message);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      alert('Failed to fetch companies');
    }
  };

  const getSubcompanies = async () => {
    try {
      const fetchSubcompanies = await apiRequest(config.ENDPOINT + 'get/company', {
        method: 'POST',
        body: JSON.stringify({})
      });
      const responseGetSubcompanies = await fetchSubcompanies.json();
      if (responseGetSubcompanies.status === 200) {
        setSubcompanies(responseGetSubcompanies.data);
      } else {
        alert(responseGetSubcompanies.message);
      }
    } catch (error) {
      console.error('Error fetching subcompanies:', error);
      alert('Failed to fetch subcompanies');
    }
  };

  const getQuotations = async () => {
    try {
      const requestQuotations = {};
      const fetchQuotations = await apiRequest(config.ENDPOINT + 'get/quotations', {
        method: 'POST',
        body: JSON.stringify(requestQuotations)
      });
      const responseGetQuotations = await fetchQuotations.json();
      if (responseGetQuotations.status === 200) {
        setQuotations(responseGetQuotations.data);
      } else {
        alert(responseGetQuotations.message);
      }
    } catch (error) {
      console.error('Error fetching quotations:', error);
      alert('Failed to fetch quotations');
    }
  };

  const getUsers = async () => {
    try {
      const fetchUsers = await apiRequest(config.ENDPOINT + 'get/user', {
        method: 'POST',
        body: JSON.stringify({})
      });
      const responseGetUsers = await fetchUsers.json();
      if (responseGetUsers.status === 200) {
        setUsers(responseGetUsers.data);
      } else {
        alert(responseGetUsers.message);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to fetch users');
    }
  };

  const getQuotationStats = async () => {
    try {
      setIsLoadingQuotationStats(true);
      const fetchQuotationStats = await apiRequest(config.ENDPOINT + 'stats/quotations', {
        method: 'POST',
        body: JSON.stringify({
          dateStart: dateStart,
          dateEnd: dateEnd,
        })
      });
      const responseQuotationStats = await fetchQuotationStats.json();
      if (responseQuotationStats.status === 200) {
        setGrossRevenue(responseQuotationStats.data?.gross_revenue || 0);
        setGrossProfit(responseQuotationStats.data?.gross_profit || 0);
        setWonQuotationCount(responseQuotationStats.data?.won_quotation_count || 0);
        setWinRate(responseQuotationStats.data?.win_rate || 0);
        setMomRevenueValue(responseQuotationStats.data?.mom_revenue_value || 0);
        setMomRevenuePercent(responseQuotationStats.data?.mom_revenue_percent);
        setMomProfitValue(responseQuotationStats.data?.mom_profit_value || 0);
        setMomProfitPercent(responseQuotationStats.data?.mom_profit_percent);
        setTopSalesPerformanceData(responseQuotationStats.data?.top_sales_performance || []);
        setTopProductPerformanceData(responseQuotationStats.data?.top_product_performance || []);
        setTopCompanyGroupsData(responseQuotationStats.data?.top_company_groups || []);
        setProductTypeDistributionData(responseQuotationStats.data?.product_type_distribution || []);
        setProductCategoryDistributionData(responseQuotationStats.data?.product_category_distribution || []);
        setOrderTypeDistributionData(responseQuotationStats.data?.order_type_distribution || []);
        setPaymentTypeDistributionData(responseQuotationStats.data?.payment_type_distribution || []);
        setQuotationStatusCountData(responseQuotationStats.data?.quotation_status_count || []);
        setDailyRevenueProfitData(responseQuotationStats.data?.daily_revenue_profit_this_month || []);
        setCumulativeRevenueProfitData(responseQuotationStats.data?.cumulative_revenue_profit_this_month || []);
      } else {
        alert(responseQuotationStats.message);
      }
    } catch (e) {
      console.error("Error while fetching quotation stats:", e);
      alert("Failed to fetch quotation stats.");
    } finally {
      setIsLoadingQuotationStats(false);
    }
  };

  const getLeadStats = async () => {
    try {
      setIsLoadingLeadStats(true);
      const fetchLeadStats = await apiRequest(config.ENDPOINT + 'stats/leads', {
        method: 'POST',
        body: JSON.stringify({
          dateStart: dateStart,
          dateEnd: dateEnd,
        })
      });
      const responseLeadStats = await fetchLeadStats.json();
      if (responseLeadStats.status === 200) {
        setQualifiedLeadsCount(responseLeadStats.data?.qualified_leads_count || 0);
        setQualifiedLeadsAmount(responseLeadStats.data?.qualified_leads_amount || 0);
      } else {
        alert(responseLeadStats.message);
      }
    } catch (e) {
      console.error("Error while fetching lead stats:", e);
      alert("Failed to fetch lead stats.");
    } finally {
      setIsLoadingLeadStats(false);
    }
  };

  const getContractScheduleAlerts = async () => {
    try {
      setIsLoadingScheduleAlerts(true);
      const fetchScheduleAlerts = await apiRequest(config.ENDPOINT + 'get/contract_schedule_table', {
        method: 'POST',
        body: JSON.stringify({
          created_by: crmUser?.user_level === 'User' ? crmUser?.user_keyid : null,
          schedule_status: 'Active',
          without_quotation: true,
          only_upcoming_due: true,
        })
      });
      const responseScheduleAlerts = await fetchScheduleAlerts.json();
      if (responseScheduleAlerts.status === 200) {
        setScheduleAlerts(responseScheduleAlerts.data || []);
      } else {
        alert(responseScheduleAlerts.message);
      }
    } catch (e) {
      console.error("Error while fetching contract schedule alerts:", e);
      alert("Failed to fetch upcoming and due schedules.");
    } finally {
      setIsLoadingScheduleAlerts(false);
    }
  };

  const getDateFilterLabel = () => {
    const startDate = dateStart ? format(new Date(dateStart), "d MMM yyyy") : "-";
    const endDate = dateEnd ? format(new Date(dateEnd), "d MMM yyyy") : "-";
    return `${dateFilter} (${startDate} - ${endDate})`;
  };

  const formatMomPercent = (value) => {
    if (value === null || value === undefined) {
      return "N/A";
    }
    const trimTrailingZeros = (numericString) => numericString.replace(/\.?0+$/, "");
    const prefix = value > 0 ? "+" : "";
    return `${prefix}${trimTrailingZeros(Number(value).toFixed(2))}%`;
  };

  const formatRate = (value) => {
    if (value === null || value === undefined) {
      return "0%";
    }
    return `${Number(value).toFixed(2).replace(/\.?0+$/, "")}%`;
  };

  const chartYAxisFormatter = (value) => compactNumberFormat(value);

  const chartDateFormatter = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return format(date, "d MMM");
  };

  const formatDateLabel = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return format(date, "d MMM yyyy");
  };

  const chartCategoryFormatter = (value) => {
    if (!value) return "-";
    const label = String(value);
    return label.length > 14 ? `${label.slice(0, 14)}...` : label;
  };
  const pieColors = ["#0d6efd", "#198754", "#fd7e14", "#6f42c1", "#20c997", "#dc3545", "#ffc107", "#0dcaf0"];
  const preparePieChartData = (rows = []) => {
    const sortedRows = [...rows].sort((a, b) => (b.total || 0) - (a.total || 0));
    if (sortedRows.length <= 6) {
      return sortedRows;
    }
    const topRows = sortedRows.slice(0, 5);
    const othersTotal = sortedRows.slice(5).reduce((total, row) => total + (Number(row.total) || 0), 0);
    return [...topRows, { name: "Others", total: othersTotal }];
  };
  const pieLegendFormatter = (value) => chartCategoryFormatter(value);

  const productTypePieData = preparePieChartData(productTypeDistributionData);
  const productCategoryPieData = preparePieChartData(productCategoryDistributionData);
  const orderTypePieData = preparePieChartData(orderTypeDistributionData);
  const paymentTypePieData = preparePieChartData(paymentTypeDistributionData);

  const openCustomRangeModal = () => {
    setModalDateStart(dateStart);
    setModalDateEnd(dateEnd);
    setShowDateModal(true);
  };

  const applyCustomRange = () => {
    if (!modalDateStart || !modalDateEnd) {
      alert("Please select start and end date.");
      return;
    }
    if (new Date(modalDateStart) > new Date(modalDateEnd)) {
      alert("Start date cannot be after end date.");
      return;
    }
    setDateStart(modalDateStart);
    setDateEnd(modalDateEnd);
    setShowDateModal(false);
  };

  const changeDateFilter = (filter) => {
    setDateFilter(filter);
    if (filter === "Custom Range") {
      openCustomRangeModal();
    }
  };

  return (
    <div id="dashboard">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar"/></a>
            </div>
            <hr className="m-0"/>
            <Menu active="dashboard"/>
          </div>
          <div className="col-md-10">
            <Header/>
            <hr className="m-0"/>
            <div className="main">
              <div className="p-4">

                <section className={"mb-0 " + (crmUser.user_level === 'User' ? '' : 'd-none')}>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="card border-0">
                        <div className="card-body d-flex align-items-center justify-content-center gap-5">
                          <i className="bi bi-buildings-fill text-gold fs-48"/>
                          <div>
                            <strong className="fs-38">{companies ? (subcompanies && (companies.length + subcompanies.length)) : <Spinner/>}</strong>
                            <br/>
                            <p>Total Clients</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card border-0">
                        <div className="card-body d-flex align-items-center justify-content-center gap-5">
                          <i className="bi bi-inbox-fill text-gold fs-48"/>
                          <div>
                            <strong className="fs-38">{quotations ? quotations.length : <Spinner/>}</strong>
                            <br/>
                            <p>Total Quotations</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card border-0">
                        <div className="card-body d-flex align-items-center justify-content-center gap-5">
                          <i className="bi bi-people-fill text-gold fs-48"/>
                          <div>
                            <strong className="fs-38">{users ? users.length : <Spinner/>}</strong>
                            <br/>
                            <p>Total Users</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card border-0 mt-3">
                    <div className="card-body">
                      <h5 className="mb-3">Upcoming Contract Schedules</h5>
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th className="bg-gold" style={{ width: "60px" }}>#</th>
                              <th className="bg-gold text-start">Schedule Date</th>
                              <th className="bg-gold text-start">Status</th>
                              <th className="bg-gold text-start">Contract</th>
                              <th className="bg-gold text-start">Company</th>
                              <th className="bg-gold text-start">Subcompany</th>
                              <th className="bg-gold text-start">Sales Person</th>
                              <th className="bg-gold"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoadingScheduleAlerts ? (
                              <tr>
                                <td colSpan="8" className="text-center py-4">
                                  <Spinner size="sm" className="me-2" />
                                  Loading schedules...
                                </td>
                              </tr>
                            ) : scheduleAlerts.length > 0 ? (
                              scheduleAlerts.map((item, index) => (
                                <tr key={item.id_contract_schedule}>
                                  <td className="text-center">{index + 1}</td>
                                  <td>{formatDateLabel(item.schedule_start_date)}</td>
                                  <td>
                                    <span
                                      className={
                                        "badge " + (
                                          item.schedule_label === "Due"
                                            ? "text-bg-danger"
                                            : item.schedule_label === "Upcoming"
                                              ? "text-bg-warning"
                                              : "text-bg-secondary"
                                        )
                                      }
                                    >
                                      {item.schedule_label || "-"}
                                    </span>
                                  </td>
                                  <td>{item.contract_name || "-"}</td>
                                  <td>{item.company_name || "-"}</td>
                                  <td>{item.subcompany_name || "-"}</td>
                                  <td>{item.sales_name || "-"}</td>
                                  <td valign="middle" align="right"><button className="btn btn-sm bg-gold" onClick={() => window.location.href = `/client/contract/schedule/${item.id_contract_schedule}`}><i className="bi bi-eye"/></button></td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="8" className="text-center text-secondary py-4">
                                  No upcoming or due schedules without quotation.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </section>

                <section className={"mb-0 " + (crmUser.user_level === 'User' ? 'd-none' : '')}>
                  <div className="d-flex align-items-center justify-content-between gap-3 mb-4">
                    <h2 className="mb-0">CuanCuan Dashboard</h2>
                    <div className="d-flex align-items-center gap-2">
                      <span className="small text-secondary">Date</span>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" id="dashboard-date-filter">
                          {getDateFilterLabel()}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => changeDateFilter("Today")}>Today</Dropdown.Item>
                          <Dropdown.Item onClick={() => changeDateFilter("This Week")}>This Week</Dropdown.Item>
                          <Dropdown.Item onClick={() => changeDateFilter("This Month")}>This Month</Dropdown.Item>
                          <Dropdown.Item onClick={() => changeDateFilter("This Year")}>This Year</Dropdown.Item>
                          <Dropdown.Item onClick={() => changeDateFilter("Custom Range")}>Custom Range</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                      {dateFilter === "Custom Range" && (
                        <button
                          className="btn btn-outline-secondary"
                          onClick={openCustomRangeModal}
                        >
                          Change Date
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="card border-0">
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-3 col-sm-6">
                          <div className="border rounded p-3 h-100">
                            <p className="mb-2 text-secondary small">Gross Revenue (IDR)</p>
                            <h4 className="mb-0">
                              {isLoadingQuotationStats ? <Spinner size="sm"/> : compactNumberFormat(grossRevenue || 0)}
                            </h4>
                          </div>
                        </div>
                        <div className="col-md-3 col-sm-6">
                          <div className="border rounded p-3 h-100">
                            <p className="mb-2 text-secondary small">Gross Profit (IDR)</p>
                            <h4 className="mb-0">
                              {isLoadingQuotationStats ? <Spinner size="sm"/> : compactNumberFormat(grossProfit || 0)}
                            </h4>
                          </div>
                        </div>
                        <div className="col-md-3 col-sm-6">
                          <div className="border rounded p-3 h-100">
                            <p className="mb-2 text-secondary small">MoM Revenue (IDR)</p>
                            <h4 className="mb-1">
                              {isLoadingQuotationStats ? <Spinner size="sm"/> : compactNumberFormat(momRevenueValue || 0)}
                            </h4>
                            <p className={"mb-0 small " + (
                              momRevenuePercent === null || momRevenuePercent === undefined
                                ? "text-secondary"
                                : momRevenuePercent >= 0
                                  ? "text-success"
                                  : "text-danger"
                            )}>
                              {isLoadingQuotationStats ? "-" : formatMomPercent(momRevenuePercent)}
                            </p>
                          </div>
                        </div>
                        <div className="col-md-3 col-sm-6">
                          <div className="border rounded p-3 h-100">
                            <p className="mb-2 text-secondary small">MoM Profit (IDR)</p>
                            <h4 className="mb-1">
                              {isLoadingQuotationStats ? <Spinner size="sm"/> : compactNumberFormat(momProfitValue || 0)}
                            </h4>
                            <p className={"mb-0 small " + (
                              momProfitPercent === null || momProfitPercent === undefined
                                ? "text-secondary"
                                : momProfitPercent >= 0
                                  ? "text-success"
                                  : "text-danger"
                            )}>
                              {isLoadingQuotationStats ? "-" : formatMomPercent(momProfitPercent)}
                            </p>
                          </div>
                        </div>
                        <div className="col-md-3 col-sm-6">
                          <div className="border rounded p-3 h-100">
                            <p className="mb-2 text-secondary small">Qualified Leads Count</p>
                            <h4 className="mb-0">
                              {isLoadingLeadStats ? <Spinner size="sm"/> : compactNumberFormat(qualifiedLeadsCount || 0)}
                            </h4>
                          </div>
                        </div>
                        <div className="col-md-3 col-sm-6">
                          <div className="border rounded p-3 h-100">
                            <p className="mb-2 text-secondary small">Qualified Leads Amount (IDR)</p>
                            <h4 className="mb-0">
                              {isLoadingLeadStats ? <Spinner size="sm"/> : compactNumberFormat(qualifiedLeadsAmount || 0)}
                            </h4>
                          </div>
                        </div>
                        <div className="col-md-3 col-sm-6">
                          <div className="border rounded p-3 h-100">
                            <p className="mb-2 text-secondary small">Won Quotations</p>
                            <h4 className="mb-0">
                              {isLoadingQuotationStats ? <Spinner size="sm"/> : compactNumberFormat(wonQuotationCount || 0)}
                            </h4>
                          </div>
                        </div>
                        <div className="col-md-3 col-sm-6">
                          <div className="border rounded p-3 h-100">
                            <p className="mb-2 text-secondary small">Win Rate</p>
                            <h4 className="mb-0">
                              {isLoadingQuotationStats ? <Spinner size="sm"/> : formatRate(winRate)}
                            </h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row g-3 mt-1">
                    <div className="col-md-6">
                      <div className="card border-0 h-100">
                        <div className="card-body">
                          <h5 className="mb-3 text-center">Daily Revenue and Profit This Month</h5>
                          <div style={{ width: "100%", height: 300 }}>
                            {isLoadingQuotationStats ? (
                              <div className="d-flex align-items-center justify-content-center h-100">
                                <Spinner />
                              </div>
                            ) : (
                              <ResponsiveContainer>
                                <LineChart data={dailyRevenueProfitData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="date" tickFormatter={chartDateFormatter} />
                                  <YAxis tickFormatter={chartYAxisFormatter} />
                                  <Tooltip
                                    labelFormatter={chartDateFormatter}
                                    formatter={(value, name) => [compactNumberFormat(value), name]}
                                  />
                                  <Legend />
                                  <Line
                                    type="monotone"
                                    dataKey="revenue"
                                    name="Revenue"
                                    stroke="#0d6efd"
                                    strokeWidth={2}
                                    dot={false}
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="profit"
                                    name="Profit"
                                    stroke="#198754"
                                    strokeWidth={2}
                                    dot={false}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card border-0 h-100">
                        <div className="card-body">
                          <h5 className="mb-3 text-center">Cumulative Revenue and Profit This Month</h5>
                          <div style={{ width: "100%", height: 300 }}>
                            {isLoadingQuotationStats ? (
                              <div className="d-flex align-items-center justify-content-center h-100">
                                <Spinner />
                              </div>
                            ) : (
                              <ResponsiveContainer>
                                <LineChart data={cumulativeRevenueProfitData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="date" tickFormatter={chartDateFormatter} />
                                  <YAxis tickFormatter={chartYAxisFormatter} />
                                  <Tooltip
                                    labelFormatter={chartDateFormatter}
                                    formatter={(value, name) => [compactNumberFormat(value), name]}
                                  />
                                  <Legend />
                                  <Line
                                    type="monotone"
                                    dataKey="revenue"
                                    name="Revenue"
                                    stroke="#0d6efd"
                                    strokeWidth={2}
                                    dot={false}
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="profit"
                                    name="Profit"
                                    stroke="#198754"
                                    strokeWidth={2}
                                    dot={false}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row g-3 mt-1">
                    <div className="col-md-6">
                      <div className="card border-0 h-100">
                        <div className="card-body">
                          <h5 className="mb-3 text-center">Top Sales Performance</h5>
                          <div style={{ width: "100%", height: 340 }}>
                            {isLoadingQuotationStats ? (
                              <div className="d-flex align-items-center justify-content-center h-100">
                                <Spinner />
                              </div>
                            ) : (
                              <ResponsiveContainer>
                                <BarChart
                                  data={topSalesPerformanceData}
                                  layout="vertical"
                                  margin={{ top: 8, right: 48, left: 8, bottom: 8 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis type="number" tickFormatter={chartYAxisFormatter} />
                                  <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={120}
                                    tickFormatter={chartCategoryFormatter}
                                  />
                                  <Tooltip formatter={(value) => [compactNumberFormat(value), "Total"]} />
                                  <Bar dataKey="total" fill="#0d6efd" radius={[0, 6, 6, 0]}>
                                    <LabelList
                                      dataKey="total"
                                      position="right"
                                      formatter={compactNumberFormat}
                                    />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card border-0 h-100">
                        <div className="card-body">
                          <h5 className="mb-3 text-center">Quotation Status Count</h5>
                          <div style={{ width: "100%", height: 340 }}>
                            {isLoadingQuotationStats ? (
                              <div className="d-flex align-items-center justify-content-center h-100">
                                <Spinner />
                              </div>
                            ) : (
                              <ResponsiveContainer>
                                <BarChart
                                  data={quotationStatusCountData}
                                  layout="vertical"
                                  margin={{ top: 8, right: 48, left: 8, bottom: 8 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis type="number" />
                                  <YAxis
                                    type="category"
                                    dataKey="status"
                                    width={120}
                                    tickFormatter={chartCategoryFormatter}
                                  />
                                  <Tooltip formatter={(value) => [compactNumberFormat(value), "Total"]} />
                                  <Bar dataKey="total" fill="#198754" radius={[0, 6, 6, 0]}>
                                    <LabelList
                                      dataKey="total"
                                      position="right"
                                      formatter={compactNumberFormat}
                                    />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row g-3 mt-1">
                    <div className="col-md-6">
                      <div className="card border-0 h-100">
                        <div className="card-body">
                          <h5 className="mb-3 text-center">Top Product Performance</h5>
                          <div style={{ width: "100%", height: 340 }}>
                            {isLoadingQuotationStats ? (
                              <div className="d-flex align-items-center justify-content-center h-100">
                                <Spinner />
                              </div>
                            ) : (
                              <ResponsiveContainer>
                                <BarChart
                                  data={topProductPerformanceData}
                                  layout="vertical"
                                  margin={{ top: 8, right: 48, left: 8, bottom: 8 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis type="number" tickFormatter={chartYAxisFormatter} />
                                  <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={120}
                                    tickFormatter={chartCategoryFormatter}
                                  />
                                  <Tooltip formatter={(value) => [compactNumberFormat(value), "Total"]} />
                                  <Bar dataKey="total" fill="#fd7e14" radius={[0, 6, 6, 0]}>
                                    <LabelList
                                      dataKey="total"
                                      position="right"
                                      formatter={compactNumberFormat}
                                    />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card border-0 h-100">
                        <div className="card-body">
                          <h5 className="mb-3 text-center">Top Company Groups Performance</h5>
                          <div style={{ width: "100%", height: 340 }}>
                            {isLoadingQuotationStats ? (
                              <div className="d-flex align-items-center justify-content-center h-100">
                                <Spinner />
                              </div>
                            ) : (
                              <ResponsiveContainer>
                                <BarChart
                                  data={topCompanyGroupsData}
                                  layout="vertical"
                                  margin={{ top: 8, right: 48, left: 8, bottom: 8 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis type="number" tickFormatter={chartYAxisFormatter} />
                                  <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={120}
                                    tickFormatter={chartCategoryFormatter}
                                  />
                                  <Tooltip formatter={(value) => [compactNumberFormat(value), "Total"]} />
                                  <Bar dataKey="total" fill="#6f42c1" radius={[0, 6, 6, 0]}>
                                    <LabelList
                                      dataKey="total"
                                      position="right"
                                      formatter={compactNumberFormat}
                                    />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row g-3 mt-1">
                    <div className="col-md-6 col-sm-6">
                      <div className="card border-0 h-100">
                        <div className="card-body">
                          <h6 className="mb-3 text-center">Product Type Distribution</h6>
                          <div style={{ width: "100%", height: 300 }}>
                            {isLoadingQuotationStats ? (
                              <div className="d-flex align-items-center justify-content-center h-100">
                                <Spinner />
                              </div>
                            ) : (
                              <ResponsiveContainer>
                                <PieChart>
                                  <Pie
                                    data={productTypePieData}
                                    dataKey="total"
                                    nameKey="name"
                                    cx="40%"
                                    cy="50%"
                                    outerRadius="70%"
                                    minAngle={2}
                                  >
                                    {productTypePieData.map((entry, index) => (
                                      <Cell key={`product-type-${entry.name}`} fill={pieColors[index % pieColors.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value, name, item) => [compactNumberFormat(value), item?.payload?.name || name]} />
                                  <Legend
                                    align="right"
                                    verticalAlign="middle"
                                    layout="vertical"
                                    wrapperStyle={{ width: "44%", fontSize: "12px" }}
                                    formatter={pieLegendFormatter}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-sm-6">
                      <div className="card border-0 h-100">
                        <div className="card-body">
                          <h6 className="mb-3 text-center">Product Category Distribution</h6>
                          <div style={{ width: "100%", height: 300 }}>
                            {isLoadingQuotationStats ? (
                              <div className="d-flex align-items-center justify-content-center h-100">
                                <Spinner />
                              </div>
                            ) : (
                              <ResponsiveContainer>
                                <PieChart>
                                  <Pie
                                    data={productCategoryPieData}
                                    dataKey="total"
                                    nameKey="name"
                                    cx="40%"
                                    cy="50%"
                                    outerRadius="70%"
                                    minAngle={2}
                                  >
                                    {productCategoryPieData.map((entry, index) => (
                                      <Cell key={`product-category-${entry.name}`} fill={pieColors[index % pieColors.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value, name, item) => [compactNumberFormat(value), item?.payload?.name || name]} />
                                  <Legend
                                    align="right"
                                    verticalAlign="middle"
                                    layout="vertical"
                                    wrapperStyle={{ width: "44%", fontSize: "12px" }}
                                    formatter={pieLegendFormatter}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-sm-6">
                      <div className="card border-0 h-100">
                        <div className="card-body">
                          <h6 className="mb-3 text-center">Order Type Distribution</h6>
                          <div style={{ width: "100%", height: 360 }}>
                            {isLoadingQuotationStats ? (
                              <div className="d-flex align-items-center justify-content-center h-100">
                                <Spinner />
                              </div>
                            ) : (
                              <ResponsiveContainer>
                                <PieChart>
                                  <Pie
                                    data={orderTypePieData}
                                    dataKey="total"
                                    nameKey="name"
                                    cx="40%"
                                    cy="50%"
                                    outerRadius="70%"
                                    minAngle={2}
                                  >
                                    {orderTypePieData.map((entry, index) => (
                                      <Cell key={`order-type-${entry.name}`} fill={pieColors[index % pieColors.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value, name, item) => [compactNumberFormat(value), item?.payload?.name || name]} />
                                  <Legend
                                    align="right"
                                    verticalAlign="middle"
                                    layout="vertical"
                                    wrapperStyle={{ width: "44%", fontSize: "12px" }}
                                    formatter={pieLegendFormatter}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-sm-6">
                      <div className="card border-0 h-100">
                        <div className="card-body">
                          <h6 className="mb-3 text-center">Payment Type Distribution</h6>
                          <div style={{ width: "100%", height: 360 }}>
                            {isLoadingQuotationStats ? (
                              <div className="d-flex align-items-center justify-content-center h-100">
                                <Spinner />
                              </div>
                            ) : (
                              <ResponsiveContainer>
                                <PieChart>
                                  <Pie
                                    data={paymentTypePieData}
                                    dataKey="total"
                                    nameKey="name"
                                    cx="40%"
                                    cy="50%"
                                    outerRadius="70%"
                                    minAngle={2}
                                  >
                                    {paymentTypePieData.map((entry, index) => (
                                      <Cell key={`payment-type-${entry.name}`} fill={pieColors[index % pieColors.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value, name, item) => [compactNumberFormat(value), item?.payload?.name || name]} />
                                  <Legend
                                    align="right"
                                    verticalAlign="middle"
                                    layout="vertical"
                                    wrapperStyle={{ width: "44%", fontSize: "12px" }}
                                    formatter={pieLegendFormatter}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <Modal show={showDateModal} onHide={() => setShowDateModal(false)} centered>
                  <Modal.Header closeButton>
                    <Modal.Title>Custom Date Range</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label">Start Date</label>
                        <input
                          className="form-control"
                          type="date"
                          value={modalDateStart}
                          onChange={(e) => setModalDateStart(e.target.value)}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">End Date</label>
                        <input
                          className="form-control"
                          type="date"
                          value={modalDateEnd}
                          onChange={(e) => setModalDateEnd(e.target.value)}
                        />
                      </div>
                    </div>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowDateModal(false)}>
                      Cancel
                    </Button>
                    <Button variant="primary" onClick={applyCustomRange}>
                      Apply
                    </Button>
                  </Modal.Footer>
                </Modal>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
